"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── 2-1  Stock Ownership Diagram ────────────────────────────────────

const STRINGS: Record<
  Locale,
  {
    rights: { label: string; desc: string }[];
    company: string;
    yourShare: string;
    shares: string;
    carveOut: string;
    ownershipTitle: string;
    ownershipDescription: string;
    buyPrice: string;
    sellPrice: string;
    difference: string;
    dividendInterest: string;
    capitalTitle: string;
    capitalDescription: string;
    incomeTitle: string;
    incomeDescription: string;
  }
> = {
  ja: {
    rights: [
      { label: "議決権", desc: "経営に意見する権利" },
      { label: "配当の権利", desc: "利益の分配を受ける" },
      { label: "残余財産の権利", desc: "解散時に資産を受け取る" },
    ],
    company: "会社",
    yourShare: "あなたの持分",
    shares: "100株",
    carveOut: "全体のうち一部を切り出して持つ",
    ownershipTitle: "株を持つ = 会社の一部を持つ",
    ownershipDescription: "株は画面の数字ではなく、会社の結果に参加する商品として見ると整理しやすい",
    buyPrice: "買値",
    sellPrice: "売値",
    difference: "差額",
    dividendInterest: "配当・利息",
    capitalTitle: "値上がり益",
    capitalDescription: "買値より高く売れた差額がリターンになる",
    incomeTitle: "インカム",
    incomeDescription: "保有中に受け取る配当や利息",
  },
  en: {
    rights: [
      { label: "Voting rights", desc: "A say in how the company is run" },
      { label: "Right to dividends", desc: "A share of the profits" },
      { label: "Right to residual assets", desc: "A share of assets if it winds up" },
    ],
    company: "Company",
    yourShare: "Your stake",
    shares: "100 shares",
    carveOut: "You hold a slice carved out of the whole",
    ownershipTitle: "Owning shares = owning part of a company",
    ownershipDescription:
      "Shares are easier to think about as a stake in how the company does, not as a number on a screen",
    buyPrice: "Buy",
    sellPrice: "Sell",
    difference: "Gain",
    dividendInterest: "Dividends & interest",
    capitalTitle: "Capital gain",
    capitalDescription: "The gap between your buy price and a higher sell price is the return",
    incomeTitle: "Income",
    incomeDescription: "Dividends and interest received while you hold",
  },
};

const GRID = { cell: 24, gap: 3, cols: 5, rows: 4 } as const;

function CompanyShareGrid() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.sky;

  // Grid layout — left third of the diagram
  const gridX = 28;
  const gridY = 40;
  const gridW = GRID.cols * GRID.cell + (GRID.cols - 1) * GRID.gap;
  const gridH = GRID.rows * GRID.cell + (GRID.rows - 1) * GRID.gap;

  // The "cut" cell (bottom-right)
  const cutR = GRID.rows - 1;
  const cutC = GRID.cols - 1;
  const cutX = gridX + cutC * (GRID.cell + GRID.gap);
  const cutY = gridY + cutR * (GRID.cell + GRID.gap);
  const cutCx = cutX + GRID.cell / 2;
  const cutCy = cutY + GRID.cell / 2;

  // Landed share — center of diagram
  const shareSize = 52;
  const shareX = 240;
  const shareY = 52;
  const shareCx = shareX + shareSize / 2;
  const shareCy = shareY + shareSize / 2;

  // Rights — right third of diagram
  const rights = t.rights;
  const rightsX = 370;
  const rightsGap = 44;
  const rightsTopY = shareCy - ((rights.length - 1) * rightsGap) / 2;

  return (
    <svg viewBox="0 0 560 190" className="mx-auto w-full max-w-2xl">
      {/* Company label */}
      <text x={gridX + gridW / 2} y={24} textAnchor="middle" fontSize="13" fill="currentColor" fontWeight="600">
        {t.company}
      </text>

      {/* Company outline */}
      <rect
        x={gridX - 8}
        y={gridY - 8}
        width={gridW + 16}
        height={gridH + 16}
        rx={10}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        opacity={0.15}
        strokeDasharray="5 4"
      />

      {/* Share grid */}
      {Array.from({ length: GRID.rows }).flatMap((_, r) =>
        Array.from({ length: GRID.cols }).map((_, col) => {
          const isCut = r === cutR && col === cutC;
          const x = gridX + col * (GRID.cell + GRID.gap);
          const y = gridY + r * (GRID.cell + GRID.gap);
          return (
            <rect
              key={`${r}-${col}`}
              x={x}
              y={y}
              width={GRID.cell}
              height={GRID.cell}
              rx={5}
              fill={isCut ? "none" : c}
              opacity={isCut ? 1 : 0.22}
              stroke={c}
              strokeWidth={isCut ? "1.5" : "0"}
              strokeOpacity={0.4}
              strokeDasharray={isCut ? "4 3" : "none"}
            />
          );
        })
      )}

      {/* Arrow: cut cell → landed share */}
      <path
        d={`M ${cutCx + 6} ${cutCy} C ${cutCx + 50} ${cutCy}, ${shareX - 50} ${shareCy}, ${shareX - 6} ${shareCy}`}
        fill="none"
        stroke={c}
        strokeWidth="1.5"
        strokeDasharray="5 4"
        opacity={0.5}
      />
      <path
        d={`M ${shareX - 13} ${shareCy - 4.5} L ${shareX - 5} ${shareCy} L ${shareX - 13} ${shareCy + 4.5}`}
        fill="none"
        stroke={c}
        strokeWidth="1.5"
        opacity={0.5}
      />

      {/* "Your share" label */}
      <text x={shareCx} y={shareY - 12} textAnchor="middle" fontSize="13" fill="currentColor" fontWeight="600">
        {t.yourShare}
      </text>

      {/* Landed share */}
      <rect
        x={shareX}
        y={shareY}
        width={shareSize}
        height={shareSize}
        rx={10}
        fill={c}
        opacity={0.25}
        stroke={c}
        strokeWidth="2"
        strokeOpacity={0.6}
      />
      <text x={shareCx} y={shareCy + 5} textAnchor="middle" fontSize="14" fill={c} fontWeight="700">
        {t.shares}
      </text>

      {/* Connector: share → rights branch */}
      <line
        x1={shareX + shareSize}
        y1={shareCy}
        x2={rightsX - 10}
        y2={shareCy}
        stroke="currentColor"
        strokeWidth="1.2"
        opacity={0.18}
      />

      {/* Rights vertical branch */}
      <line
        x1={rightsX - 10}
        y1={rightsTopY}
        x2={rightsX - 10}
        y2={rightsTopY + (rights.length - 1) * rightsGap}
        stroke="currentColor"
        strokeWidth="1.2"
        opacity={0.18}
      />

      {rights.map((r, i) => {
        const ry = rightsTopY + i * rightsGap;
        return (
          <g key={r.label}>
            <line
              x1={rightsX - 10}
              y1={ry}
              x2={rightsX}
              y2={ry}
              stroke="currentColor"
              strokeWidth="1.2"
              opacity={0.18}
            />
            <circle cx={rightsX + 5} cy={ry} r={3} fill={c} opacity={0.45} />
            <text x={rightsX + 14} y={ry - 4} fontSize="12" fill={c} fontWeight="600">
              {r.label}
            </text>
            <text x={rightsX + 14} y={ry + 10} fontSize="9.5" fill="currentColor" opacity={0.5}>
              {r.desc}
            </text>
          </g>
        );
      })}

      {/* Bottom annotation */}
      <text
        x={gridX + gridW / 2}
        y={gridY + gridH + 24}
        textAnchor="middle"
        fontSize="10"
        fill="currentColor"
        opacity={0.5}
      >
        {t.carveOut}
      </text>
    </svg>
  );
}

export function DocStockOwnershipDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard title={t.ownershipTitle} description={t.ownershipDescription}>
      <CompanyShareGrid />
    </IllustrationCard>
  );
}

// ── 2-1  Stock Return Diagram ───────────────────────────────────────

function CapitalGainIllustration() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.emerald;
  return (
    <svg viewBox="0 0 200 90" className="h-32 w-full">
      <line x1={15} y1={20} x2={185} y2={20} stroke="currentColor" strokeDasharray="3 6" opacity={0.14} />
      <line x1={15} y1={70} x2={185} y2={70} stroke="currentColor" strokeDasharray="3 6" opacity={0.14} />
      <path
        d="M 20 62 Q 60 56, 80 46 Q 110 34, 145 26 Q 165 22, 180 20"
        fill="none"
        stroke={c}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <rect x="14" y="68" width="32" height="14" rx="4" fill={c} opacity={0.25} />
      <text x="30" y="78" textAnchor="middle" fontSize="8" fill={c} fontWeight="600">
        {t.buyPrice}
      </text>
      <rect x="156" y="6" width="32" height="14" rx="4" fill={c} opacity={0.25} />
      <text x="172" y="16" textAnchor="middle" fontSize="8" fill={c} fontWeight="600">
        {t.sellPrice}
      </text>
      {/* Measure arrow for the gain */}
      <g opacity={0.65}>
        <line x1={192} y1={20} x2={192} y2={62} stroke={c} strokeWidth="1.5" />
        <line x1={189} y1={20} x2={195} y2={20} stroke={c} strokeWidth="1.5" />
        <line x1={189} y1={62} x2={195} y2={62} stroke={c} strokeWidth="1.5" />
        <text
          x={198}
          y={43}
          textAnchor="start"
          dominantBaseline="central"
          fontSize="7"
          fill={c}
          fontWeight="500"
          opacity={0.9}
        >
          {t.difference}
        </text>
      </g>
    </svg>
  );
}

function IncomeIllustration() {
  const t = STRINGS[useDocLocale()];
  const c = COLOR.emerald;
  const bars = [30, 62, 94, 126, 158];
  return (
    <svg viewBox="0 0 200 90" className="h-32 w-full">
      <line x1={15} y1={72} x2={185} y2={72} stroke="currentColor" opacity={0.18} />
      <path
        d="M 15 50 Q 50 48, 65 46 Q 100 43, 135 40 Q 155 38, 185 36"
        fill="none"
        stroke={c}
        strokeWidth="2"
        strokeLinecap="round"
        opacity={0.65}
      />
      {bars.map((x, i) => (
        <rect
          key={x}
          x={x - 7}
          y={50}
          width={14}
          height={22}
          rx={3.5}
          fill={c}
          opacity={0.45 + i * 0.08}
          stroke={c}
          strokeWidth="1"
          strokeOpacity={0.65}
        />
      ))}
      <text x={100} y={84} textAnchor="middle" fontSize="7" fill={c} fontWeight="600" opacity={0.9}>
        {t.dividendInterest}
      </text>
    </svg>
  );
}

export function DocStockReturnDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <IllustrationCard title={t.capitalTitle} description={t.capitalDescription}>
        <CapitalGainIllustration />
      </IllustrationCard>
      <IllustrationCard title={t.incomeTitle} description={t.incomeDescription}>
        <IncomeIllustration />
      </IllustrationCard>
    </div>
  );
}
